import type { AudioClip, Clip, MidiClip, Project } from './types';
import { clipEndTicks, sourceSecondsBetween } from './selectors';
import { sliceWarp } from './warpMap';
import { recutSource } from './sourceSpan';

/**
 * Taking a time slice out of a clip.
 *
 * Comping is built from this: promoting a range of a take into the comp lane
 * means slicing that range out of the take and slicing the same range *away*
 * from whatever was there before. Splitting is the same arithmetic seen from a
 * different angle.
 *
 * Every slice is non-destructive. An audio slice moves the clip's `offsetSec`
 * into its untouched source asset — from whichever end the clip is heard from,
 * which is `recutSource`'s business — a MIDI slice keeps the notes it contains
 * and shortens the ones that straddle the boundary. Nothing is ever rewritten.
 */

/** The part of `clip` that lies inside `[fromTick, toTick)`, or null if none does. */
export function sliceClip(
  project: Project,
  clip: Clip,
  fromTick: number,
  toTick: number,
  newId: string,
): Clip | null {
  const clipStart = clip.startTicks;
  const clipEnd = clipEndTicks(project, clip);
  const start = Math.max(clipStart, Math.round(fromTick));
  const end = Math.min(clipEnd, Math.round(toTick));
  if (end - start < 1) return null;

  if (clip.kind === 'midi') return sliceMidi(clip, start, end, newId);
  return sliceAudio(project, clip, start, end, newId);
}

function sliceAudio(project: Project, clip: AudioClip, start: number, end: number, newId: string): AudioClip | null {
  // Source seconds consumed before the slice begins, and across it. Asked of
  // the clip rather than computed from a rate, because a clip that follows the
  // tempo consumes its source in beats, not in seconds.
  const leadSec = sourceSecondsBetween(project, clip, clip.startTicks, start);
  const spanSec = sourceSecondsBetween(project, clip, start, end);
  const durationSec = Math.min(spanSec, Math.max(0, clip.durationSec - leadSec));
  if (durationSec <= 0) return null;

  // Fades belong to the clip's own edges, so a slice taken out of the middle
  // keeps neither: reapplying them would fade in and out mid-phrase.
  const keepsStart = start <= clip.startTicks;
  const keepsEnd = end >= clipEndTicks(project, clip);

  // Markers move into the new region's coordinates, the ones outside it are
  // dropped, and the slice keeps every beat where it was — which on a bent
  // curve means refitting its tempo. See `sliceWarp`.
  return {
    ...clip,
    id: newId,
    startTicks: start,
    ...recutSource(project, clip, leadSec, durationSec),
    ...sliceWarp(clip, leadSec, durationSec),
    fadeIn: keepsStart ? clip.fadeIn : { ...clip.fadeIn, durationSec: 0 },
    fadeOut: keepsEnd ? clip.fadeOut : { ...clip.fadeOut, durationSec: 0 },
  };
}

function sliceMidi(clip: MidiClip, start: number, end: number, newId: string): MidiClip | null {
  const shift = start - clip.startTicks;
  const length = end - start;

  const notes = clip.notes
    .filter((note) => note.startTicks + note.durationTicks > shift && note.startTicks < shift + length)
    .map((note) => {
      const noteStart = Math.max(0, note.startTicks - shift);
      // A note straddling either boundary is shortened to what remains inside.
      const noteEnd = Math.min(length, note.startTicks + note.durationTicks - shift);
      return { ...note, startTicks: noteStart, durationTicks: Math.max(1, noteEnd - noteStart) };
    });

  return {
    ...clip,
    id: newId,
    startTicks: start,
    durationTicks: length,
    notes,
    // A slice is a fixed span of the timeline, so it stops looping: repeating
    // it would sound nothing like the take it came from.
    loop: false,
    loopLengthTicks: length,
  };
}

/**
 * What remains of a clip once `[fromTick, toTick)` is taken out of it.
 *
 * Removing the middle of a clip leaves two pieces, which is why this returns a
 * list. `ids` supplies identifiers for the pieces in order; the first reuses
 * the original clip's id so an untouched-at-the-start clip keeps its identity.
 */
export function subtractRange(
  project: Project,
  clip: Clip,
  fromTick: number,
  toTick: number,
  ids: readonly string[],
): Clip[] {
  const clipStart = clip.startTicks;
  const clipEnd = clipEndTicks(project, clip);
  const from = Math.round(fromTick);
  const to = Math.round(toTick);

  // No overlap: the clip survives untouched.
  if (to <= clipStart || from >= clipEnd) return [clip];
  // Entirely covered: nothing survives.
  if (from <= clipStart && to >= clipEnd) return [];

  const pieces: Clip[] = [];
  let index = 0;
  const nextId = (): string => ids[index++] ?? `${clip.id}_${index}`;

  if (from > clipStart) {
    const head = sliceClip(project, clip, clipStart, from, clip.id);
    if (head) pieces.push(head);
  }
  if (to < clipEnd) {
    const tail = sliceClip(project, clip, to, clipEnd, pieces.length === 0 ? clip.id : nextId());
    if (tail) pieces.push(tail);
  }
  return pieces;
}

/** Highest take lane in use on a track. 0 means only the comp lane exists. */
export function maxTakeLane(project: Project, trackId: string): number {
  const track = project.tracks[trackId];
  if (!track) return 0;
  let highest = 0;
  for (const clipId of track.clipIds) {
    const clip = project.clips[clipId];
    if (clip && clip.takeLane > highest) highest = clip.takeLane;
  }
  return highest;
}

/** Clips on one lane of a track, in position order. */
export function clipsOnLane(project: Project, trackId: string, lane: number): Clip[] {
  const track = project.tracks[trackId];
  if (!track) return [];
  return track.clipIds
    .map((id) => project.clips[id])
    .filter((clip): clip is Clip => Boolean(clip) && clip.takeLane === lane)
    .sort((a, b) => a.startTicks - b.startTicks);
}
